import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CompressIcon from '@mui/icons-material/Compress';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import { useTheme } from '@mui/material/styles';
import { POST } from '../../../configs/api';
import { api } from '../../../utils/constants';
import AiPromptTemplatesBar from './AiPromptTemplatesBar';
import { buildPromptSelectionPayload } from './AiPromptTemplatesHelper';
import { getAiActionButtonSx } from './modernFormFieldStyles';

const MIN_TEXT_LENGTH = 3;
const MAX_TEXT_LENGTH = 10000;

const PROPOSAL_PROMPT_KINDS = [
  'proposal_refine',
  'proposal_shorten',
  'proposal_items',
];

/**
 * True when the row is still the create-form placeholder
 * ({ type: 'labor', description: '', quantity: 1, unit_price: 0 }).
 */
export const isUntouchedDefaultBillableItem = (item) => {
  if (!item || typeof item !== 'object') return false;
  const type = String(item.type || 'labor').trim().toLowerCase();
  const description = String(item.description || '').trim();
  const quantity = Number(item.quantity);
  const rate = Number(item.unit_price);
  return (
    type === 'labor'
    && !description
    && quantity === 1
    && (rate === 0 || Number.isNaN(rate))
  );
};

/**
 * Append generated rows. If the only existing row is the untouched default, replace it.
 */
export const appendGeneratedBillableItems = (existingItems, generatedItems) => {
  const generated = Array.isArray(generatedItems) ? generatedItems : [];
  if (!generated.length) return Array.isArray(existingItems) ? existingItems : [];

  const existing = Array.isArray(existingItems) ? existingItems : [];
  if (existing.length === 1 && isUntouchedDefaultBillableItem(existing[0])) {
    return [...generated];
  }
  return [...existing, ...generated];
};

const getAiButtonSx = (isDarkMode, isBusy) => getAiActionButtonSx(isDarkMode, isBusy);

/**
 * AI helpers for the proposal form: refine description, extract billable items, or both.
 *
 * Layout A: Write with AI | Generate line items | Both, plus Make shorter after a successful write.
 */
export default function GenerateProposalButton({
  proposalText,
  allowedTypes = [],
  onTextGenerated,
  onItemsGenerated,
  disabled = false,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [loadingKind, setLoadingKind] = useState(null);
  const [error, setError] = useState('');
  const [promptSelection, setPromptSelection] = useState({});

  const seedRef = useRef('');
  const lastGeneratedRef = useRef('');

  const current = (proposalText || '').trim();
  const untouchedSinceGenerate = current && current === lastGeneratedRef.current;
  const seedText = untouchedSinceGenerate ? seedRef.current : current;
  const hasUsableText = seedText.length >= MIN_TEXT_LENGTH;
  const loading = Boolean(loadingKind);

  const applyTextResult = (mode, sourceText, nextText) => {
    if (mode !== 'shorten') {
      seedRef.current = sourceText;
    }
    lastGeneratedRef.current = nextText;
    onTextGenerated?.(nextText);
  };

  const run = async (mode) => {
    setError('');

    if (mode === 'shorten') {
      if (!untouchedSinceGenerate) {
        setError('Generate a description first, then you can make it shorter.');
        return;
      }
    } else if (!hasUsableText) {
      setError(`Enter at least ${MIN_TEXT_LENGTH} characters in the proposal description first.`);
      return;
    }

    const sourceText = mode === 'shorten' ? current : seedText;
    const body = {
      proposal_text: sourceText.slice(0, MAX_TEXT_LENGTH),
      mode,
    };

    if (mode === 'extract_items' || mode === 'both') {
      const types = (allowedTypes || [])
        .map((type) => String(type || '').trim())
        .filter(Boolean)
        .slice(0, 40);
      if (types.length) body.allowed_types = types;
    }

    if (mode === 'refine_text') {
      Object.assign(body, buildPromptSelectionPayload(promptSelection.proposal_refine));
    } else if (mode === 'shorten') {
      Object.assign(body, buildPromptSelectionPayload(promptSelection.proposal_shorten));
    } else if (mode === 'extract_items') {
      Object.assign(body, buildPromptSelectionPayload(promptSelection.proposal_items));
    } else if (mode === 'both') {
      Object.assign(body, buildPromptSelectionPayload(promptSelection.proposal_refine, 'text'));
      Object.assign(body, buildPromptSelectionPayload(promptSelection.proposal_items, 'items'));
    }

    setLoadingKind(mode);
    const { status, data, message } = await POST(api.generateProposalContent, {}, body);
    setLoadingKind(null);

    if (status !== 200 || !data) {
      setError(message || data?.message || 'Could not generate proposal content. Please try again.');
      return;
    }

    if ((mode === 'refine_text' || mode === 'shorten' || mode === 'both') && data.proposal_text) {
      applyTextResult(mode === 'shorten' ? 'shorten' : 'refine_text', sourceText, data.proposal_text);
    }

    if ((mode === 'extract_items' || mode === 'both') && Array.isArray(data.items) && data.items.length) {
      onItemsGenerated?.(data.items);
    } else if (mode === 'extract_items') {
      setError(message || data?.message || 'Could not extract billable items. Please try again.');
      return;
    }

    if (mode === 'both' && !data.proposal_text && !data.items?.length) {
      setError(message || 'Could not generate proposal content. Please try again.');
    }
  };

  const busyLabel = {
    refine_text: 'Writing...',
    shorten: 'Shortening...',
    extract_items: 'Extracting...',
    both: 'Working...',
  };

  const hint = (() => {
    if (error) return error;
    if (loadingKind === 'both') return 'Refines the description, then extracts line items. Takes a few seconds.';
    if (loadingKind === 'extract_items') return 'This takes a few seconds.';
    if (loadingKind === 'shorten') return 'This takes a few seconds.';
    if (loading) return 'This takes a few seconds.';
    if (untouchedSinceGenerate) {
      return 'Rewrite from your original note, shorten this draft, extract line items, or do both.';
    }
    if (hasUsableText) {
      return 'Refine the description, extract billable items, or do both. Review before saving.';
    }
    return 'Type a short proposal note to enable AI helpers.';
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.75, mb: 2 }}>
      <AiPromptTemplatesBar
        kinds={PROPOSAL_PROMPT_KINDS}
        selection={promptSelection}
        onSelectionChange={setPromptSelection}
        disabled={disabled || loading}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      <Button
        type="button"
        variant="contained"
        size="small"
        disableElevation
        disabled={disabled || loading || !hasUsableText}
        onClick={() => run('refine_text')}
        startIcon={
          loadingKind === 'refine_text' ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <AutoAwesomeIcon sx={{ fontSize: '1.05rem !important' }} />
          )
        }
        sx={getAiButtonSx(isDarkMode, loadingKind === 'refine_text')}
      >
        <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
          {loadingKind === 'refine_text' ? busyLabel.refine_text : untouchedSinceGenerate ? 'Rewrite' : 'Write with AI'}
        </Box>
      </Button>

      <Button
        type="button"
        variant="contained"
        size="small"
        disableElevation
        disabled={disabled || loading || !hasUsableText}
        onClick={() => run('extract_items')}
        startIcon={
          loadingKind === 'extract_items' ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <PlaylistAddIcon sx={{ fontSize: '1.05rem !important' }} />
          )
        }
        sx={getAiButtonSx(isDarkMode, loadingKind === 'extract_items')}
      >
        <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
          {loadingKind === 'extract_items' ? busyLabel.extract_items : 'Generate line items'}
        </Box>
      </Button>

      <Button
        type="button"
        variant="contained"
        size="small"
        disableElevation
        disabled={disabled || loading || !hasUsableText}
        onClick={() => run('both')}
        startIcon={
          loadingKind === 'both' ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <DynamicFeedIcon sx={{ fontSize: '1.05rem !important' }} />
          )
        }
        sx={getAiButtonSx(isDarkMode, loadingKind === 'both')}
      >
        <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
          {loadingKind === 'both' ? busyLabel.both : 'Both'}
        </Box>
      </Button>

      {untouchedSinceGenerate && (
        <Button
          type="button"
          variant="contained"
          size="small"
          disableElevation
          disabled={disabled || loading}
          onClick={() => run('shorten')}
          startIcon={
            loadingKind === 'shorten' ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <CompressIcon sx={{ fontSize: '1.05rem !important' }} />
            )
          }
          sx={getAiButtonSx(isDarkMode, loadingKind === 'shorten')}
        >
          <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
            {loadingKind === 'shorten' ? busyLabel.shorten : 'Make shorter'}
          </Box>
        </Button>
      )}

      <Typography
        variant="caption"
        sx={{ color: error ? 'error.main' : 'text.secondary', fontSize: '0.75rem' }}
      >
        {hint}
      </Typography>
      </Box>
    </Box>
  );
}
